import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { api } from '@/api/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'

interface SpaceCategory {
  _id: string
  name: string
  description: string
  imageUrl: string
  gradientColors: {
    start: string
    end: string
  }
  textColor?: string
  coverImageDefault?: string
  logoDefault?: string
  order: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export default function SpaceCategoriesPage() {
  const [categories, setCategories] = useState<SpaceCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<SpaceCategory | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    gradientColors: {
      start: '#667eea',
      end: '#764ba2',
    },
    textColor: '#FFFFFF',
    coverImageDefault: '',
    logoDefault: '',
    isActive: true,
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      // Use admin endpoint to get ALL categories including inactive ones
      const response = await api.get('/api/admin/space-categories')
      setCategories(response.data.data)
    } catch (error) {
      toast.error('Failed to fetch categories')
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name)
      formDataToSend.append('description', formData.description)

      // Ensure gradientColors is properly serialized as a JSON string
      console.log('formData.gradientColors type:', typeof formData.gradientColors)
      console.log('formData.gradientColors value:', formData.gradientColors)

      // Make sure we have a proper object before stringifying
      if (typeof formData.gradientColors === 'object' && formData.gradientColors.start && formData.gradientColors.end) {
        const gradientColorsString = JSON.stringify(formData.gradientColors)
        console.log('Stringified gradientColors:', gradientColorsString)
        formDataToSend.append('gradientColors', gradientColorsString)
      } else {
        console.error('Invalid gradientColors format:', formData.gradientColors)
        // Use default values if something is wrong
        const defaultGradient = JSON.stringify({ start: '#667eea', end: '#764ba2' })
        formDataToSend.append('gradientColors', defaultGradient)
      }

      formDataToSend.append('textColor', formData.textColor)
      formDataToSend.append('isActive', String(formData.isActive))

      if (imageFile) {
        formDataToSend.append('image', imageFile)
      } else if (formData.imageUrl) {
        formDataToSend.append('imageUrl', formData.imageUrl)
      }

      if (coverImageFile) {
        formDataToSend.append('coverImageDefault', coverImageFile)
      } else if (formData.coverImageDefault) {
        formDataToSend.append('coverImageDefault', formData.coverImageDefault)
      }

      if (logoFile) {
        formDataToSend.append('logoDefault', logoFile)
      } else if (formData.logoDefault) {
        formDataToSend.append('logoDefault', formData.logoDefault)
      }

      if (editingCategory) {
        const response = await api.put(`/api/space-categories/${editingCategory._id}`, formDataToSend)
        const updatedCategory = response.data.data
        setCategories(categories.map(cat => cat._id === updatedCategory._id ? updatedCategory : cat))
        toast.success('Category updated successfully')
      } else {
        const response = await api.post('/api/space-categories', formDataToSend)
        setCategories([...categories, response.data.data])
        toast.success('Category created successfully')
      }

      handleCloseDialog()
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || 'Failed to save category')
      console.error('Error saving category:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return

    try {
      await api.delete(`/api/space-categories/${id}`)
      setCategories(categories.filter(cat => cat._id !== id))
      toast.success('Category deleted successfully')
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || 'Failed to delete category')
      console.error('Error deleting category:', error)
    }
  }

  const handleReorder = async (categoryId: string, direction: 'up' | 'down') => {
    const index = categories.findIndex(cat => cat._id === categoryId)
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === categories.length - 1)) {
      return
    }

    const newCategories = [...categories]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    const temp = newCategories[index]
    newCategories[index] = newCategories[swapIndex]
    newCategories[swapIndex] = temp

    // Update order values
    const categoryOrders = newCategories.map((cat, idx) => ({
      id: cat._id,
      order: idx,
    }))

    try {
      await api.post('/api/space-categories/reorder', { categoryOrders })
      setCategories(newCategories)
      toast.success('Categories reordered successfully')
    } catch (error) {
      toast.error('Failed to reorder categories')
      console.error('Error reordering categories:', error)
    }
  }

  const handleEdit = (category: SpaceCategory) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description,
      imageUrl: category.imageUrl,
      gradientColors: category.gradientColors,
      textColor: category.textColor || '#FFFFFF',
      coverImageDefault: category.coverImageDefault || '',
      logoDefault: category.logoDefault || '',
      isActive: category.isActive,
    })
    setImageFile(null)
    setCoverImageFile(null)
    setLogoFile(null)
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingCategory(null)
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      gradientColors: {
        start: '#667eea',
        end: '#764ba2',
      },
      textColor: '#FFFFFF',
      coverImageDefault: '',
      logoDefault: '',
      isActive: true,
    })
    setImageFile(null)
    setCoverImageFile(null)
    setLogoFile(null)
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Space Categories</h1>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Category
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">Order</TableHead>
            <TableHead>Image</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Gradient</TableHead>
            <TableHead>Text Color</TableHead>
            <TableHead>Default Images</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((category, index) => (
            <TableRow key={category._id}>
              <TableCell>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReorder(category._id, 'up')}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReorder(category._id, 'down')}
                    disabled={index === categories.length - 1}
                  >
                    ↓
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <div
                  className="w-16 h-16 rounded-lg flex items-center justify-center relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${category.gradientColors.start}, ${category.gradientColors.end})`,
                  }}
                >
                  {category.imageUrl ? (
                    <img
                      src={category.imageUrl}
                      alt={category.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <span
                      className="text-xs font-semibold"
                      style={{
                        color: category.textColor || '#FFFFFF',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {category.name.substring(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-medium">{category.name}</TableCell>
              <TableCell className="max-w-xs truncate">{category.description}</TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <div
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: category.gradientColors.start }}
                  />
                  <div
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: category.gradientColors.end }}
                  />
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded border-2 border-gray-300"
                    style={{ backgroundColor: category.textColor || '#FFFFFF' }}
                  />
                  <span className="text-xs font-mono">{category.textColor || '#FFFFFF'}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  {category.coverImageDefault && (
                    <div className="relative group">
                      <img
                        src={category.coverImageDefault}
                        alt="Cover"
                        className="w-10 h-10 object-cover rounded border"
                        title="Default Cover Image"
                      />
                      <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Cover
                      </span>
                    </div>
                  )}
                  {category.logoDefault && (
                    <div className="relative group">
                      <img
                        src={category.logoDefault}
                        alt="Logo"
                        className="w-10 h-10 object-cover rounded border"
                        title="Default Logo"
                      />
                      <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Logo
                      </span>
                    </div>
                  )}
                  {!category.coverImageDefault && !category.logoDefault && (
                    <span className="text-gray-400 text-sm">None</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={category.isActive ? 'default' : 'secondary'}>
                  {category.isActive ? (
                    <>
                      <Eye className="w-3 h-3 mr-1" /> Active
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3 mr-1" /> Inactive
                    </>
                  )}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(category)}
                  className="mr-2"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(category._id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory
                  ? 'Update the details of the space category.'
                  : 'Create a new space category for organizing spaces.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="image" className="text-right">
                  Image
                </Label>
                <div className="col-span-3">
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  />
                  {editingCategory && !imageFile && (
                    <p className="text-sm text-gray-500 mt-1">
                      Current image will be kept if no new image is selected
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Gradient Colors</Label>
                <div className="col-span-3 flex space-x-2">
                  <div>
                    <Label htmlFor="startColor" className="text-xs">Start</Label>
                    <Input
                      id="startColor"
                      type="color"
                      value={formData.gradientColors.start}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gradientColors: { ...formData.gradientColors, start: e.target.value },
                        })
                      }
                      className="w-20"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endColor" className="text-xs">End</Label>
                    <Input
                      id="endColor"
                      type="color"
                      value={formData.gradientColors.end}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gradientColors: { ...formData.gradientColors, end: e.target.value },
                        })
                      }
                      className="w-20"
                    />
                  </div>
                  <div
                    className="w-32 h-10 rounded-md border mt-5 flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${formData.gradientColors.start}, ${formData.gradientColors.end})`,
                      color: formData.textColor,
                    }}
                  >
                    <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>Preview</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="textColor" className="text-right">
                  Text Color
                </Label>
                <div className="col-span-3 flex space-x-2">
                  <Input
                    id="textColor"
                    type="color"
                    value={formData.textColor}
                    onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                    className="w-20"
                  />
                  <Input
                    type="text"
                    value={formData.textColor}
                    onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                    placeholder="#FFFFFF"
                    className="w-28 font-mono text-sm"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                  <div className="flex space-x-1 items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, textColor: '#FFFFFF' })}
                    >
                      White
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, textColor: '#000000' })}
                    >
                      Black
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="coverImage" className="text-right">
                  Default Cover Image
                </Label>
                <div className="col-span-3">
                  <Input
                    id="coverImage"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCoverImageFile(e.target.files?.[0] || null)}
                  />
                  {editingCategory && !coverImageFile && formData.coverImageDefault && (
                    <div className="mt-2">
                      <img
                        src={formData.coverImageDefault}
                        alt="Current cover"
                        className="h-20 w-32 object-cover rounded border"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Current cover image (will be kept if no new image is selected)
                      </p>
                    </div>
                  )}
                  {!editingCategory && (
                    <p className="text-sm text-gray-500 mt-1">
                      Default cover image for spaces in this category
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="logoImage" className="text-right">
                  Default Logo
                </Label>
                <div className="col-span-3">
                  <Input
                    id="logoImage"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                  {editingCategory && !logoFile && formData.logoDefault && (
                    <div className="mt-2">
                      <img
                        src={formData.logoDefault}
                        alt="Current logo"
                        className="h-20 w-20 object-cover rounded border"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Current logo (will be kept if no new logo is selected)
                      </p>
                    </div>
                  )}
                  {!editingCategory && (
                    <p className="text-sm text-gray-500 mt-1">
                      Default logo for spaces in this category
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="isActive" className="text-right">
                  Status
                </Label>
                <div className="col-span-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}